/**
 * 🩺 [Phase 96 v2 — 2026-07-09] Widget Santé Data étendu
 *
 * Affiche l'état des 6 modules audit dans une grille 2×3 (desktop) / stack (mobile) :
 *   1. Heures Réelles (Phase 98)
 *   2. Rappels SMS RDV (Phase 99)
 *   3. Migrations (Phase 88+)
 *   4. CA Signé (Phase 97)
 *   5. BT orphelins (Phase 84 — déplacé depuis DataHealthBlock/Vue d'ensemble)
 *   6. Employés désynchronisés (Phase 96 v2 nouveau — user↔employee)
 *
 * Objectif : détecter en 3 secondes un bug silencieux (comme Bug C Pause PM
 * resté caché 2 mois).
 *
 * Backend : GET /api/dashboard/sante-data (cache 5 min côté serveur).
 * Refresh auto toutes les 5 minutes côté client + bouton refresh manuel.
 *
 * Drill-down formaté par module (tableau lisible), plus de JSON brut.
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  RefreshCw, HeartPulse, CheckCircle2, AlertCircle, AlertTriangle,
  Users, Clock, MessageSquare, PackageSearch, DollarSign, Database,
} from 'lucide-react';
import { DataHealthBlock } from '@/pages/dashboard/DataHealthBlock';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTO_REFRESH_MS = 5 * 60 * 1000; // 5 minutes

const STATUT_CONFIG = {
  vert: {
    label: 'OK',
    color: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    dot: 'bg-emerald-500',
    cardBorder: 'border-emerald-200',
    cardBg: 'bg-emerald-50/40',
    icon: CheckCircle2,
  },
  orange: {
    label: 'Attention',
    color: 'bg-amber-100 text-amber-700 border-amber-300',
    dot: 'bg-amber-500',
    cardBorder: 'border-amber-300',
    cardBg: 'bg-amber-50',
    icon: AlertTriangle,
  },
  rouge: {
    label: 'Anomalies',
    color: 'bg-red-100 text-red-700 border-red-300',
    dot: 'bg-red-500',
    cardBorder: 'border-red-300',
    cardBg: 'bg-red-50',
    icon: AlertCircle,
  },
};

// Icônes par module (fallback Database)
const MODULE_ICONS = {
  heures_reelles: Clock,
  rappels_sms: MessageSquare,
  migrations: Database,
  ca_signe: DollarSign,
  bt_orphelins: PackageSearch,
  employes_desync: Users,
};

function formatRelativeTime(iso) {
  if (!iso) return '';
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return `il y a ${Math.floor(diff / 86400)} j`;
  } catch {
    return '';
  }
}

export function SanteDataWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const intervalRef = useRef(null);

  const fetchSanteData = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      // L'intercepteur axios global (lib/api.js) injecte automatiquement le
      // token Bearer depuis localStorage/sessionStorage — pas de header manuel.
      const r = await axios.get(
        `${API_URL}/api/dashboard/sante-data${force ? '?force=true' : ''}`
      );
      setData(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSanteData(false);
    // Refresh auto toutes les 5 min
    intervalRef.current = setInterval(() => {
      fetchSanteData(false);
    }, AUTO_REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  if (loading && !data) {
    return (
      <Card className="mt-6" data-testid="sante-data-widget-loading">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-pink-500 animate-pulse" />
            <span className="font-semibold text-lg">Santé Data</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-24 bg-slate-100 animate-pulse rounded-lg"
                data-testid={`sante-data-skeleton-${i}`}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error && !data) {
    return (
      <Card className="mt-6 border-red-200 bg-red-50" data-testid="sante-data-widget-error">
        <CardContent className="py-4 text-sm text-red-700 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium">🩺 Santé Data — Erreur de chargement</p>
            <p className="text-xs mt-1">{error}</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchSanteData(true)}>
            Réessayer
          </Button>
        </CardContent>
      </Card>
    );
  }

  const globalCfg = STATUT_CONFIG[data.global_status] || STATUT_CONFIG.vert;
  const GlobalIcon = globalCfg.icon;

  return (
    <>
      <Card className="mt-6 animate-fade-in delay-250" data-testid="sante-data-widget">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <HeartPulse className="w-5 h-5 text-pink-500" />
                Santé Data
                <Badge className={`ml-1 ${globalCfg.color}`} data-testid="sante-data-global-badge">
                  <GlobalIcon className="w-3 h-3 mr-1" />
                  {globalCfg.label}
                </Badge>
              </h2>
              <p className="text-xs text-slate-500 mt-1">
                6 modules audit surveillés en continu ·{' '}
                <span data-testid="sante-data-checked-at">
                  Vérifié {formatRelativeTime(data.checked_at)}
                </span>
                {data.from_cache && ` (cache ${Math.floor((data.cache_age_seconds || 0) / 60)} min)`}
                {' · '}Auto-refresh 5 min
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchSanteData(true)}
              disabled={loading}
              data-testid="sante-data-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Rafraîchir
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(data.modules || []).map((m) => {
              const cfg = STATUT_CONFIG[m.statut] || STATUT_CONFIG.vert;
              const Icon = cfg.icon;
              const ModuleIcon = MODULE_ICONS[m.id] || Database;
              const clickable = m.count > 0;
              return (
                <div
                  key={m.id}
                  className={`p-4 border-2 rounded-lg transition-all ${cfg.cardBorder} ${cfg.cardBg} ${
                    clickable ? 'cursor-pointer hover:shadow-md' : ''
                  }`}
                  onClick={clickable ? () => setSelectedModule(m) : undefined}
                  data-testid={`sante-data-module-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <ModuleIcon className="w-4 h-4 text-slate-600 shrink-0" />
                      <span className="font-semibold text-sm truncate">{m.titre}</span>
                    </div>
                    <Icon className={`w-4 h-4 shrink-0 ${
                      m.statut === 'vert' ? 'text-emerald-600' :
                        m.statut === 'orange' ? 'text-amber-600' : 'text-red-600'
                    }`} />
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span
                      className={`text-3xl font-bold leading-none ${
                        m.statut === 'vert' ? 'text-emerald-700' :
                          m.statut === 'orange' ? 'text-amber-700' : 'text-red-700'
                      }`}
                      data-testid={`sante-data-count-${m.id}`}
                    >
                      {m.count < 0 ? '—' : m.count}
                    </span>
                    <span className="text-xs text-slate-500">
                      {clickable ? '· cliquer pour détails' : ''}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1.5 leading-snug break-words">
                    {m.description}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Drill-down modal — formaté par module */}
      {selectedModule && (
        <SanteDataDrillDown
          module={selectedModule}
          onClose={() => setSelectedModule(null)}
        />
      )}
    </>
  );
}

/**
 * Drill-down modal — formatage par type de module.
 * BT orphelins : réutilise DataHealthBlock (composant riche avec actions Recréer/Supprimer)
 * Employés désync : table nom/email/anomalies/actions
 * Autres : table générique par type de données
 */
function SanteDataDrillDown({ module, onClose }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Cas spécial : BT orphelins → on rend DataHealthBlock directement (pas de fetch ici)
    if (module.id === 'bt_orphelins') {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        // Intercepteur axios global gère l'auth automatiquement.
        const r = await axios.get(`${API_URL}${module.drill_down_url}`);
        setDetails(r.data);
      } catch (err) {
        setDetails({ error: err?.response?.data?.detail || err?.message });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [module.id, module.drill_down_url]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="sante-data-drill-down"
    >
      <div
        className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b p-4 flex items-center justify-between shrink-0">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-pink-500" />
            Détails — {module.titre}
            <Badge variant="outline" className="ml-2 text-xs">
              {module.count} {module.count > 1 ? 'items' : 'item'}
            </Badge>
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="sante-data-drill-close">
            Fermer
          </Button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 bg-slate-50">
          {module.id === 'bt_orphelins' ? (
            <DataHealthBlock deptView="PRQC" />
          ) : loading ? (
            <p className="text-slate-500 text-center py-8">Chargement...</p>
          ) : details?.error ? (
            <p className="text-red-600 text-sm">Erreur : {details.error}</p>
          ) : (
            <DrillDownTable module={module} details={details} />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Formatage du drill-down par type de module (tableau lisible).
 */
function DrillDownTable({ module, details }) {
  if (!details) return null;

  // Employés désynchronisés : table nom/email/rôle/anomalies
  if (module.id === 'employes_desync') {
    const results = (details.results || []).filter(r => (r.anomalies || []).length > 0);
    if (results.length === 0) {
      return <p className="text-emerald-700 text-sm py-4">✅ Aucun employé désynchronisé.</p>;
    }
    return (
      <div>
        <p className="text-xs text-slate-500 mb-3">
          {details.total_users_scanned} users scannés · {details.users_with_anomalies} avec anomalies · {details.users_ok} OK
        </p>
        <div className="overflow-x-auto bg-white rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-xs uppercase text-slate-600">
              <tr>
                <th className="text-left py-2 px-3">Utilisateur</th>
                <th className="text-left py-2 px-3">Email</th>
                <th className="text-left py-2 px-3">Rôle</th>
                <th className="text-left py-2 px-3">Anomalies détectées</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, idx) => {
                const criticalCount = r.anomalies.filter(a => a.severity === 'critical').length;
                return (
                  <tr key={r.user_id || idx} className="border-t hover:bg-slate-50" data-testid={`drill-employes-row-${r.user_id}`}>
                    <td className="py-2 px-3 font-medium">{r.user_nom}</td>
                    <td className="py-2 px-3 text-xs text-slate-600">{r.user_email}</td>
                    <td className="py-2 px-3">
                      <Badge variant="outline" className="text-xs">{r.user_role || '—'}</Badge>
                    </td>
                    <td className="py-2 px-3">
                      {criticalCount > 0 && (
                        <Badge className="bg-red-100 text-red-700 border-red-300 mr-1 text-xs">
                          {criticalCount} critique{criticalCount > 1 ? 's' : ''}
                        </Badge>
                      )}
                      <div className="mt-1 space-y-0.5">
                        {r.anomalies.map((a, i) => (
                          <div key={i} className="text-xs">
                            <span className={a.severity === 'critical' ? 'text-red-700 font-medium' : 'text-amber-700'}>
                              [{a.code}]
                            </span>{' '}
                            <span className="text-slate-600">{a.message}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Heures Réelles : liste BT dérivés
  if (module.id === 'heures_reelles') {
    const bts = details.bts_derives || details.bts || [];
    if (bts.length === 0) return <p className="text-emerald-700 text-sm py-4">✅ Aucune dérive heures détectée.</p>;
    return (
      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left py-2 px-3">BT</th>
              <th className="text-left py-2 px-3">Dossier</th>
              <th className="text-right py-2 px-3">Heures actuelles</th>
              <th className="text-right py-2 px-3">Heures vraies</th>
              <th className="text-right py-2 px-3">Δ</th>
            </tr>
          </thead>
          <tbody>
            {bts.map((b, i) => (
              <tr key={b.bt_id || i} className="border-t">
                <td className="py-2 px-3 font-mono text-xs">{b.bt_numero || (b.bt_id || '').slice(0, 8)}</td>
                <td className="py-2 px-3 text-xs">{b.dossier_nom || b.client_nom || '—'}</td>
                <td className="py-2 px-3 text-right">{Number(b.heures_actuelles ?? b.heures_reelles ?? 0).toFixed(2)}</td>
                <td className="py-2 px-3 text-right">{Number(b.heures_vraies ?? 0).toFixed(2)}</td>
                <td className="py-2 px-3 text-right font-medium text-red-700">
                  {Number(b.delta ?? ((b.heures_vraies || 0) - (b.heures_actuelles || 0))).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Rappels SMS : liste RDV ratés
  if (module.id === 'rappels_sms') {
    const missed = details.rdvs_manques || details.rdvs || [];
    if (missed.length === 0) return <p className="text-emerald-700 text-sm py-4">✅ Aucun rappel SMS raté.</p>;
    return (
      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left py-2 px-3">Date RDV</th>
              <th className="text-left py-2 px-3">Client / Titre</th>
              <th className="text-left py-2 px-3">Rappels manqués</th>
            </tr>
          </thead>
          <tbody>
            {missed.map((r, i) => (
              <tr key={r.rdv_id || i} className="border-t">
                <td className="py-2 px-3">{r.date || '—'}</td>
                <td className="py-2 px-3">{r.titre || r.client_nom || '—'}</td>
                <td className="py-2 px-3 text-xs text-red-700">
                  {r.rappels_manques ? r.rappels_manques.join(', ') : (r.type_manque || '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Migrations : liste migrations manquantes
  if (module.id === 'migrations') {
    const migrations = details.migrations || [];
    return (
      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left py-2 px-3">Migration ID</th>
              <th className="text-left py-2 px-3">Statut</th>
              <th className="text-left py-2 px-3">Dernière exécution</th>
            </tr>
          </thead>
          <tbody>
            {migrations.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-4 text-center text-slate-500">Aucune migration trackée</td>
              </tr>
            ) : (
              migrations.map((mig, i) => (
                <tr key={mig.id || i} className="border-t">
                  <td className="py-2 px-3 font-mono text-xs">{mig.id}</td>
                  <td className="py-2 px-3">
                    <Badge className={mig.applied
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-red-100 text-red-700'}>
                      {mig.applied ? 'Appliquée' : 'Manquante'}
                    </Badge>
                  </td>
                  <td className="py-2 px-3 text-xs text-slate-600">{mig.last_run_at || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // CA Signé : liste dossiers incohérents
  if (module.id === 'ca_signe') {
    const dossiers = details.dossiers_incoherents || details.incoherents || [];
    if (dossiers.length === 0) return <p className="text-emerald-700 text-sm py-4">✅ Aucune incohérence CA signé.</p>;
    return (
      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left py-2 px-3">Dossier</th>
              <th className="text-left py-2 px-3">Client</th>
              <th className="text-right py-2 px-3">Montant signé</th>
              <th className="text-left py-2 px-3">Statut soum.</th>
            </tr>
          </thead>
          <tbody>
            {dossiers.map((d, i) => (
              <tr key={d.id || i} className="border-t">
                <td className="py-2 px-3 font-mono text-xs">{d.numero_dossier || (d.id || '').slice(0, 8)}</td>
                <td className="py-2 px-3">{d.client_nom || '—'}</td>
                <td className="py-2 px-3 text-right">{Number(d.montant_soumission ?? 0).toFixed(2)} $</td>
                <td className="py-2 px-3 text-xs text-red-700">{d.soum_statut || d.raison || 'aucune acceptée'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Fallback : afficher le JSON (rare)
  return (
    <pre className="text-xs bg-white border rounded p-3 overflow-auto whitespace-pre-wrap break-words">
      {JSON.stringify(details, null, 2)}
    </pre>
  );
}

export default SanteDataWidget;
